export const cleanFromMarkdown = (text: string) =>
  text
    .replace(/\[.*?\]/g, "") // Remove content in square brackets
    .replace(/\*\*(.*?)\*\*/g, "$1") // Remove bold markdown **text**
    .replace(/\*(.*?)\*/g, "$1") // Remove italic markdown *text*
    .replace(/`(.*?)`/g, "$1") // Remove inline code markdown `text`
    .replace(/_{1,2}(.*?)_{1,2}/g, "$1") // Remove underline markdown _text_ or __text__
    .replace(/~~(.*?)~~/g, "$1") // Remove strikethrough markdown ~~text~~
    .replace(/#{1,6}\s*/g, "") // Remove headers # ## ### etc
    .replace(/^\s*[-*+]\s*/gm, "") // Remove bullet points
    .replace(/^\s*\d+\.\s+/gm, "") // Remove numbered lists
    .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1") // Remove links [text](url) -> text
    .trim();

import type { BusinessReportOutcomeResult } from "@/modules/snapshots/businessReports.types";

export const getSnapshotCellDisplayText = (text: string) => {
  const cleanedText = cleanFromMarkdown(text);
  return cleanedText.split("\n").find(line => line.trim()) || cleanedText;
};

export const getTypedSnapshotCellDisplayText = (cell: BusinessReportOutcomeResult): string => {
  if (cell.data_type === "currency") {
    if (cell.currency_amount != null && cell.currency_code != null) {
      return `${cell.currency_amount} ${cell.currency_code}`;
    }
    if (cell.normalized_is_na || cell.skip_reason === "relevancy_false") return "N/A";
  }
  if (cell.data_type === "relevancy" && cell.relevancy != null) return cell.relevancy ? "True" : "False";
  return getSnapshotCellDisplayText(cell.result);
};

const hasClassifiedRelevancy = (cell: Pick<BusinessReportOutcomeResult, "data_type" | "relevancy">) =>
  cell.data_type === "relevancy" && cell.relevancy != null;

export const isSnapshotCellPending = (
  cell: Pick<BusinessReportOutcomeResult, "result" | "error_message" | "data_type" | "relevancy">,
): boolean => cell.result === "" && !cell.error_message && !hasClassifiedRelevancy(cell);

// A persisted answer wins over a stale error_message left by a late, duplicate failure.
export const isSnapshotCellFailed = (
  cell: Pick<BusinessReportOutcomeResult, "result" | "error_message" | "data_type" | "relevancy">,
): boolean => Boolean(cell.error_message) && !cell.result && !hasClassifiedRelevancy(cell);

export const SNAPSHOT_CELL_PREVIEW_MAX_LENGTH = 50;

export const getSnapshotCellPreview = (text: string) => {
  const displayText = getSnapshotCellDisplayText(text);
  return displayText.length > SNAPSHOT_CELL_PREVIEW_MAX_LENGTH
    ? `${displayText.substring(0, SNAPSHOT_CELL_PREVIEW_MAX_LENGTH)}...`
    : displayText;
};
